<script setup lang="ts">
import { computed, ref, useTemplateRef, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

import DialogModal from '@/components/dialogs/DialogModal.vue';
import QuoteInquiryForm from '@/components/forms/QuoteInquiryForm.vue';
import CircleCheckDuotoneSolid from '@/components/svg/CircleCheckDuotoneSolid.vue';
import CircleInfoRegular from '@/components/svg/CircleInfoRegular.vue';
import EnvelopeRegular from '@/components/svg/EnvelopeRegular.vue';
import FileArrowDownRegular from '@/components/svg/FileArrowDownRegular.vue';
import XMarkSolid from '@/components/svg/XMarkSolid.vue';
import { formatCurrencyWithoutDecimal } from '@/helpers/currencyHelper.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { NewQuoteInfo } from '@/typings/quotes/NewQuotesInfo.ts';
import type { Unit } from '@/typings/units/Unit.ts';
import { isUnitDisabled } from '@/enums/UnitStatus';
import { usePdfGenerator } from '@/composables/usePdfGenerator';
import UnitCostDetails from './UnitCostDetails.vue';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});
const quoteInquiryFormComponent = useTemplateRef<InstanceType<typeof QuoteInquiryForm>>('quoteInquiryFormComponent');

const props = defineProps<{
  unit: Unit;
  actionsAreFixed?: boolean;
  quoteInfo: NewQuoteInfo;
  selectedConfigurationId?: number;
  selectedStyleId?: number;
  selectedOptionIds: number[];
  isFirstTimeHomebuyer?: boolean;
}>();

// Computed property for localized floor name (same as in UnitSummaryDetails.vue)
const floorName = computed(() => {
  const localeMapping: Record<string, string> = {
    'fr-ca': props.quoteInfo?.unit?.floorName != null ? props.quoteInfo.unit.floorName : t('common.notAvailableAbbreviated'),
    'en-ca': props.quoteInfo?.unit?.floorNameEn != null ? props.quoteInfo.unit.floorNameEn : t('common.notAvailableAbbreviated')
  };

  const localeValue = locale.value.toLowerCase();
  return localeMapping[localeValue] || localeMapping['fr-ca'];
});

// Computed properties for options (same as in UnitSummaryDetails.vue)
const optionsHeading = computed(() => {
  return props.unit.hasDefaultOptions ? `${t('unitSummary.additionalOptions')} (${t('unitSummary.preConfigured_F', 2).toLocaleLowerCase()})` : t('unitSummary.additionalOptions');
});

const filteredOptions = computed(() => {
  const options = props.quoteInfo?.options || [];
  if (props.unit.hasDefaultOptions) {
    return options.filter(option => option.quantity > 0);
  }
  return options;
});

const calculatedPrice = ref(0);
const infoPopoverIsVisible = ref(false);
const inquireQuoteInfoIsVisible = ref(false);
const inquiryQuoteSuccessfulySent = ref(false);
const userQuoteEnabled = ref(false);

const { isCreatingPdf, pdfPreparationStatus, downloadPdf, previewHtml } = usePdfGenerator();

const getAdditionalInformationPrefill = computed(() => {
  const address = locale.value.toLowerCase() === 'fr-ca' ? props.quoteInfo?.unit.fullAddress : props.quoteInfo?.unit.fullAddressEn;
  return `${t('forms.prefills.quoteInquiry.additionalInformation', {
    unit: address,
    project: props.quoteInfo?.unit.localizedProjectName,
  })}`;
});

const priceBoxTitle = computed(() => {
  return props.quoteInfo?.unit.hasDefaultOptions ? t('unitSummary.unitTotalPrice') : t('browsingApp.startingAt');
});

watch(infoPopoverIsVisible, (visible) => {
  if (visible) {
    document.addEventListener('click', onClickOutside);
    document.addEventListener('keydown', onKeyEscape);
  } else {
    document.removeEventListener('click', onClickOutside);
    document.removeEventListener('keydown', onKeyEscape);
  }
});

const dynamicPdfUrl = computed(() => {
  if (!props.quoteInfo?.unit?.id || props.selectedConfigurationId == null) {
    return null;
  }
  
  const baseUrl = '/umbraco/api/units/unit-pdf';
  const unitId = props.quoteInfo.unit.id;
  const configurationId = props.selectedConfigurationId;
  const currentLocale = locale.value || 'fr';
  
  return `${baseUrl}/${unitId}?selectedConfiguration=${configurationId}&locale=${currentLocale}`;
});

function onClickInfoButton() {
  infoPopoverIsVisible.value = !infoPopoverIsVisible.value;
}

function onClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.unitSummary-infoButton')) {
    infoPopoverIsVisible.value = false;
  }
}

function onConfirmSuccessDialog() {
  inquiryQuoteSuccessfulySent.value = false;
}

function onInquireQuoteInfo() {
  inquireQuoteInfoIsVisible.value = true;
}

function onFormSubmit() {
  if (quoteInquiryFormComponent.value) {
    quoteInquiryFormComponent.value.onSubmitForm();
  }
}

function onKeyEscape(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    infoPopoverIsVisible.value = false;
  }
}

function onSubmitSuccess() {
  inquiryQuoteSuccessfulySent.value = true;
  inquireQuoteInfoIsVisible.value = false;
}

function handleDownloadPdf() {
  downloadPdf({
    unit: props.unit,
    quoteInfo: props.quoteInfo,
    selectedConfigurationId: props.selectedConfigurationId,
    selectedOptionIds: props.selectedOptionIds,
    locale: locale.value,
    t,
    formatCurrency: (amount: number) => formatCurrencyWithoutDecimal(amount, locale),
    calculatedPrice: calculatedPrice.value,
    priceBoxTitle: priceBoxTitle.value,
    floorName: floorName.value,
    optionsHeading: optionsHeading.value,
    promotion: promotion.value,
    showCostDetails: true,
    priceSubtitle: '',
    description: description.value,
    isRentalMode: false,
    isFirstTimeHomebuyer: props.isFirstTimeHomebuyer
  });
}

function handlePreviewHtml() {
  previewHtml({
    unit: props.unit,
    quoteInfo: props.quoteInfo,
    selectedConfigurationId: props.selectedConfigurationId,
    selectedOptionIds: props.selectedOptionIds,
    locale: locale.value,
    t,
    formatCurrency: (amount: number) => formatCurrencyWithoutDecimal(amount, locale),
    calculatedPrice: calculatedPrice.value,
    priceBoxTitle: priceBoxTitle.value,
    floorName: floorName.value,
    optionsHeading: optionsHeading.value,
    promotion: promotion.value,
    showCostDetails: true,
    priceSubtitle: '',
    description: description.value,
    isRentalMode: false,
    isFirstTimeHomebuyer: props.isFirstTimeHomebuyer
  });
}

watch(inquireQuoteInfoIsVisible, (visible) => {
  if (!visible && quoteInquiryFormComponent.value) {
    quoteInquiryFormComponent.value.onResetForm();
  }
});

const promotion = computed(() => {
  const localeMapping: Record<string, string> = {
    'fr-ca': 'promotions',
    'en-ca': 'promotionsEn'
  };

  const localeValue = locale.value.toLowerCase();

  return props.unit[localeMapping[localeValue]] as string | undefined;
});

const description = computed(() => {
  const localeMapping: Record<string, string> = {
    'fr-ca': 'description',
    'en-ca': 'descriptionEn'
  };

  const localeValue = locale.value.toLowerCase();

  return props.unit[localeMapping[localeValue]] as string | undefined;
});

// Logique mobile pour la promotion
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
const isMobile = computed(() => windowWidth.value <= 1024);
const isPromotionVisible = ref(false);

// Ecouter les changements de taille de fenetre
const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateWindowWidth);
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateWindowWidth);
  }
});

function togglePromotionVisibility() {
  if (isMobile.value) {
    isPromotionVisible.value = !isPromotionVisible.value;
  }
}
</script>

<template>
  <div>
    <aside>
      <div v-if="!isUnitDisabled(unit.status)" class="priceAndPromotion">
        <!-- Prix -->
        <div class="unitSummary-priceBox">
          <h3 class="title">
            {{ priceBoxTitle }}
          </h3>
          <div class="price">
            {{ calculatedPrice === 0 ? $t('common.notAvailableAbbreviated') : formatCurrencyWithoutDecimal(calculatedPrice, locale) }}
          </div>
          <div class="note">
            <span>{{ t('unitSummary.includingRebatesAndTaxes') }}</span>
            <button class="unitSummary-infoButton"
                    @click="onClickInfoButton">
              <CircleInfoRegular class="icon" />
            </button>
            <div id="infoPopover"
                 class="unitSummary-infoPopover"
                 :hidden="!infoPopoverIsVisible">
              <span class="unitSummary-infoPopoverTitle">{{ t('unitSummary.includingRebatesAndTaxesNote') }}</span>
              <UnitCostDetails v-if="quoteInfo"
                               v-model="calculatedPrice"
                               :quote-info="quoteInfo"
                               :selected-configuration-id="selectedConfigurationId"
                               :selected-options="selectedOptionIds"
                               :is-first-time-homebuyer="isFirstTimeHomebuyer" />
            </div>
          </div>
        </div>
        <!-- Promotion -->
        <div v-if="promotion" class="unitSummary-promotion">
          <span v-if="isMobile"
                :class="{ 'tagPromotion tagPromotion--clickable': isMobile }"
                @click="togglePromotionVisibility">
            {{ t('unitSummary.promotion') }}
            <span v-if="isMobile" class="info">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                <path d="M320 112C434.9 112 528 205.1 528 320C528 434.9 434.9 528 320 528C205.1 528 112 434.9 112 320C112 205.1 205.1 112 320 112zM320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM280 400C266.7 400 256 410.7 256 424C256 437.3 266.7 448 280 448L360 448C373.3 448 384 437.3 384 424C384 410.7 373.3 400 360 400L352 400L352 312C352 298.7 341.3 288 328 288L280 288C266.7 288 256 298.7 256 312C256 325.3 266.7 336 280 336L304 336L304 400L280 400zM320 256C337.7 256 352 241.7 352 224C352 206.3 337.7 192 320 192C302.3 192 288 206.3 288 224C288 241.7 302.3 256 320 256z" />
              </svg>
            </span>
          </span>
          <div v-if="!isMobile || (isMobile && isPromotionVisible)"
               class="tagPromotion-content"
               @click="togglePromotionVisibility">
            <div class="tagPromotion-wrapper">
              <div class="header">
                <div class="title">
                  {{ t('unitSummary.promotion') }}
                </div>
                <div v-if="isMobile" class="close">
                  <XMarkSolid />
                </div>
              </div>
              <div class="description">{{ promotion }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="unitSummary-buttonGroup">
        <button :id="t('unitSummary.purchase.buttonRequestInformation')"
                class="btn unitSummary-buttonSend btn_textWhite"
                @click="onInquireQuoteInfo">
          <EnvelopeRegular />
          <span>{{ t('unitSummary.informationInquiry') }}</span>
        </button>
        <button :disabled="isCreatingPdf"
                class="btn btn_secondary unitSummary-buttonDownload"
                @click="handleDownloadPdf">
          <FileArrowDownRegular />
          <span v-if="!isCreatingPdf">{{ t('unitSummary.downloadSheet') }}</span>
          <span v-else>{{ pdfPreparationStatus || 'Génération...' }}</span>
        </button>
        <!-- Bouton pour l'aperçu HTML avant génération PDF -->
        <!-- <button v-if="dynamicPdfUrl"
                class="btn btn_light unitSummary-buttonDownload"
                @click="handlePreviewHtml"
                title="Aperçu HTML avant génération PDF">
          <span>[Preview]</span>
        </button> -->
        <button v-if="userQuoteEnabled"
                class="btn btn_light unitSummary-buttonDownload">
          <FileArrowDownRegular />
          <span>{{ t('unitSummary.downloadSubmission') }}</span>
        </button>
      </div>
    </aside>
    <teleport to="body">
      <DialogModal v-model:open="inquireQuoteInfoIsVisible"
                   class="dialogModal_form"
                   :cancel-button-label="t('browsingApp.actions.close')"
                   :confirm-button-label="t('forms.labels.submitMyInquiry', 'Soumettre')"
                   :confirm-button-id="t('unitSummary.purchase.buttonConfirmationSubmit')"
                   :pending="quoteInquiryFormComponent?.sendQuoteInfoInquiryLoading"
                   show-footer
                   @confirm="onFormSubmit">
        <template #headerContent>
          <span>{{ t('unitSummary.informationInquiryFormTitle') }}</span>
        </template>
        <!-- Section Promotion dans le modal -->
        <div v-if="promotion" class="modal-promotion">
          <div class="modal-promotion-header">
            <h3>{{ t('unitSummary.promotion') }}</h3>
          </div>
          <div class="modal-promotion-content">
            {{ promotion }}
          </div>
        </div>
        <QuoteInquiryForm ref="quoteInquiryFormComponent"
                          :quote-info="quoteInfo"
                          :default-additional-info="getAdditionalInformationPrefill"
                          :selected-configuration-id="selectedConfigurationId"
                          :selected-style-id="selectedStyleId"
                          :selected-option-ids="selectedOptionIds"
                          :is-first-time-homebuyer="isFirstTimeHomebuyer"
                          nested
                          @submit:success="onSubmitSuccess" />
      </DialogModal>
    </teleport>
    <teleport to="body">
      <DialogModal v-model:open="inquiryQuoteSuccessfulySent"
                   class="dialogModal_submissionResult"
                   :confirm-button-label="t('browsingApp.actions.close')"
                   confirm-button-id="close-success-dialog-btn"
                   hide-cancel-button
                   show-footer
                   @confirm="onConfirmSuccessDialog">
        <template #headerContent>
          <h2>{{ t('unitSummary.informationInquiryFormSuccessTitle') }}</h2>
        </template>
        <div class="dialogModal-success">
          <CircleCheckDuotoneSolid class="icon" />
          <p>{{ t('unitSummary.informationInquiryFormSuccessText') }}</p>
        </div>
      </DialogModal>
    </teleport>
  </div>
</template>

<style lang="scss">
@import '@/assets/styles/components/unitSummaryActions.scss';
</style>
